import { gsap } from "gsap"
import Experience from '../../Experience'
import pageData from '../../pageData'

export default class ContactForm {

    domElements = {
        form: document.getElementById('contact-form-container'),
        submitButton: document.getElementById('contact-submit-button'),
        formContainer: document.getElementById('contact-form-container'),
        loadingContainer: document.getElementById('contact-loading-container'),
        resultContainer: document.getElementById('contact-result-container'),
        resultMessage: document.getElementById('contact-result-message-container'),
        resultButton: document.getElementById('contact-result-button'),
        errorLines: document.querySelectorAll('.contact-error-line'),
        successLine: document.getElementById('contact-success-line'),
        chips: document.querySelectorAll('.contact-chip'),
    }

    fields = {
        name: {
            input: document.getElementById('contact-name-input-field'),
            container: document.getElementById('contact-name-input'),
        },
        email: {
            input: document.getElementById('contact-email-input-field'),
            container: document.getElementById('contact-email-input')
        },
        message: {
            input: document.getElementById('contact-message-input-field'),
            container: document.getElementById('contact-message-input')
        }
    }

    topic = null

    constructor() {
        this.experience = new Experience()
        this.sections = this.experience.ui.sections
        this.scroll = this.experience.ui.scroll
        this.sounds = this.experience.sounds

        this.addSubmitButtonEventListener()
        this.addHideErrorEventListeners()
        this.addResultButtonEventListener()
        this.addChipEventListeners()
        this.initKeyboardEvents()
    }

    //Enter and Tab move between fields, Cmd/Ctrl + Enter sends
    initKeyboardEvents() {
        this.domElements.form.addEventListener('submit', (event) => event.preventDefault())

        const fields = Object.values(this.fields)

        fields.forEach((field, index) => {
            field.input.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                    event.preventDefault()
                    this.submit()
                } else if (event.key === 'Enter' && field.input.tagName !== 'TEXTAREA') {
                    event.preventDefault()
                    this.focusNext(index)
                } else if (event.key === 'Tab') {
                    event.preventDefault()
                    this.focusNext(event.shiftKey ? index - 2 : index)
                }
            })
        })
    }

    focusNext(currentIndex) {
        //check if contact form is visible
        const contactSection = this.sections.sections.find(s => s.name === 'contact')
        if (contactSection && this.scroll.scrollY + (window.innerHeight / 3) >= contactSection.y) {
            const fields = Object.values(this.fields)
            const nextIndex = (currentIndex + 1 + fields.length) % fields.length

            setTimeout(() => fields[nextIndex].input.focus())
        }
    }

    addChipEventListeners() {
        this.domElements.chips.forEach((chip) => {
            chip.addEventListener('click', () => {
                this.sounds.play('buttonClick')

                const isSelected = chip.getAttribute('aria-pressed') === 'true'

                this.domElements.chips.forEach((otherChip) => otherChip.setAttribute('aria-pressed', 'false'))
                chip.setAttribute('aria-pressed', isSelected ? 'false' : 'true')

                this.topic = isSelected ? null : chip.dataset.topic
            })
        })
    }

    addSubmitButtonEventListener() {
        this.domElements.submitButton.addEventListener('click', () => this.submit())
    }

    submit() {
        this.sounds.play('buttonClick')

        this.hideAllErrors()

        const nameIsValid = this.fields.name.input.value.trim().length >= 2
        const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(this.fields.email.input.value.trim())
        const messageIsValid = this.fields.message.input.value.trim().length >= 10

        if (!nameIsValid) this.showError('name')
        if (!emailIsValid) this.showError('email')
        if (!messageIsValid) this.showError('message')

        if (nameIsValid && emailIsValid && messageIsValid)
            this.sendMail()
    }

    addHideErrorEventListeners() {
        const fields = Object.values(this.fields);
        const fieldsName = Object.keys(this.fields);
        fields.forEach((field, index) => {
            //Events
            field.container.addEventListener('focusin', () => this.hideError(fieldsName[index]))
            field.input.addEventListener('input', () => this.hideError(fieldsName[index]))
        })
    }

    hideAllErrors() {
        const fields = Object.keys(this.fields);
        fields.forEach((field) => this.hideError(field))
    }

    hideError(fieldName) {
        const field = this.fields[fieldName];

        field.container.querySelector('.error-label').classList.add('hide')
        field.container.classList.remove('error-container')
        field.input.removeAttribute('aria-invalid')
    }

    showError(fieldName) {
        const field = this.fields[fieldName];

        field.container.querySelector('.error-label').classList.remove('hide')
        field.container.classList.add('error-container')
        field.input.setAttribute('aria-invalid', 'true')
    }

    async sendMail() {
        //container
        this.showContainer('loading')

        //await fetch than show result
        try {
            const api = ['localhost', '127.0.0.1'].includes(location.hostname) ? 'http://127.0.0.1:4545/api/contact' : 'https://michelelaurelli.it/admin/api/contact'
            const response = await fetch(api, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    name: this.fields['name'].input.value.trim(),
                    email: this.fields['email'].input.value.trim(),
                    topic: this.topic || '',
                    message: this.fields['message'].input.value.trim(),
                    lang: pageData().lang,
                    source: location.pathname
                })
            })
            this.showResult(response)

            // Track successful submission in Google Analytics
            if (response.ok && typeof gtag !== 'undefined') {
                gtag('event', 'form_submit', {
                    'event_category': 'Contact',
                    'event_label': this.topic || 'Contact Form Success'
                })
            }
        } catch (error) {
            console.error('Form submission error:', error)

            // Track error in Google Analytics
            if (typeof gtag !== 'undefined') {
                gtag('event', 'form_error', {
                    'event_category': 'Contact',
                    'event_label': 'Contact Form Error'
                })
            }

            // Show error if fetch fails
            this.showResult({ status: 500 })
        }
    }

    hideAllContainers() {
        this.domElements.formContainer.classList.add('hide')
        this.domElements.resultContainer.classList.add('hide')
        this.domElements.loadingContainer.classList.add('hide')
    }

    showContainer(name) {
        this.hideAllContainers()

        //Fade in new container
        gsap.fromTo(this.domElements[name + 'Container'], { opacity: 0 }, { opacity: 1, duration: .2 })

        //shown new container
        this.domElements[name + 'Container'].classList.remove('hide')
    }

    showResult(result) {
        const success = Math.floor(result.status / 100) == 2

        this.showContainer('result')

        const { result: messages } = pageData().contact
        const message = success ? messages.success : messages.error

        //text
        const title = document.createElement('h4')
        title.textContent = message.title
        const text = document.createElement('p')
        text.innerHTML = message.text
        text.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'))
        this.domElements.resultMessage.replaceChildren(title, text)

        //button text
        this.domElements.resultButton.textContent = message.button

        //icon
        success ? this.showSuccessIcon() : this.showErrorIcon()
    }

    addResultButtonEventListener() {
        this.domElements.resultButton.addEventListener('click', () => {
            this.sounds.play('buttonClick')

            this.showContainer('form')

            //clear inputs if no error occurred
            if (this.domElements.errorLines[0].classList.contains('hide'))
                this.clearInputs()
        })
    }

    showSuccessIcon() {
        //Update visiblity
        this.domElements.successLine.classList.remove('hide')
        this.domElements.errorLines.forEach((line) => line.classList.add('hide'))

        this.fillLine(this.domElements.successLine)
    }

    showErrorIcon() {
        //Update visiblity
        this.domElements.successLine.classList.add('hide')
        this.domElements.errorLines.forEach((line) => line.classList.remove('hide'))

        let delay = 0
        this.domElements.errorLines.forEach((line) => {
            this.fillLine(line, delay, .3)
            delay += .3
        })
    }

    clearInputs() {
        const fields = Object.values(this.fields);
        fields.forEach((field) => field.input.value = '')

        this.topic = null
        this.domElements.chips.forEach((chip) => chip.setAttribute('aria-pressed', 'false'))
    }

    fillLine(line, delay = 0, duration = .5) {
        //check if line is rendered
        if (line.getClientRects().length != 0) {
            const lineLength = line.getTotalLength()

            //adapt strok dasharray
            line.style.strokeDasharray = lineLength

            //fill animation
            gsap.fromTo(line.style, { strokeDashoffset: lineLength }, { strokeDashoffset: 0, duration: duration, delay: delay })
        }
    }
}
